import React, { useCallback, useEffect, useMemo } from "react";
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import first from "lodash/first";
import isEmpty from "lodash/isEmpty";

import { Card, Box, Divider } from "@mui/material";

import { currencyConverter as cc } from "utils/currency";
import {
  PriceOverviewBlock,
  DriverGratuityBlock,
  BookingContactBlock,
  PaymentMethodBlock,
  TermsAndConditionsBlock,
  SubmitButton,
  YourCardWillNotBeChargedBlock,
} from "components/confirm/form/components";
import { useAutomatedPricing, useOperator } from "globals/hooks";
import { calculateCreateTripPricing } from "globals/hooks/useAutomatedPricing";
import { useIsGratuityEnabled } from "components/confirm/form/hooks/useIsGratuityEnabled";
import { CreateReservationFormState } from "../form/schemaValidation";
import ConsentSignatureSubmitButton from "components/confirm/form/components/ConsentSignatureSubmitButton";
import MeetGreetBlock from "components/confirm/form/components/MeetGreetBlock";
import { useDisplayMeetGreet } from "components/confirm/form/hooks/useDisplayMeetGreet";
import { CreateTripState } from "pages/new/context/initialState";
import { TripCategory } from "types";
import { useAutoChargePayment } from "pages/new/hooks";
import SmsConsentMessage from "components/sms-consent/SmsConsentMessage";
import { useDisplayAdditionalItems } from "components/confirm/form/hooks";
import AdditionalItemsBlock from "components/confirm/form/components/AdditionalItemsBlock";
import { usePricelessBookingContext } from "../../../context";
import { ConfirmReservationFormState } from "pages/order/components/ConfirmReservationForm/form/schemaValidation";
import LuggageCountBlock from "components/confirm/TripDetailsBlock/components/TripItineraryCard/components/AdditionalTripInfoBlock/components/AdditionalTripInfoDialog/components/LuggageCountBlock";
import { ReservationPaymentForm } from "./ReservationPaymentForm";
import type { ReservationPaymentSetupAdapter } from "pages/new/hooks/useReservationPaymentSetup";

type CreateReservationFormFieldsProps = {
  control: Control<CreateReservationFormState>;
  watch: UseFormWatch<CreateReservationFormState>;
  formId: string;
  onConsentSignatureDialogOpen: () => void;
  isSubmitting: boolean;
  trips: CreateTripState[];
  setValue: UseFormSetValue<any>;
  createReservationErrors: any;
  getValues: UseFormGetValues<any>;
  disableSubmit?: boolean;
  turnstileElement?: React.ReactNode;
  reservationPaymentSetup?: ReservationPaymentSetupAdapter;
};

function CreateReservationFormFields(props: CreateReservationFormFieldsProps) {
  const {
    control,
    watch,
    formId,
    onConsentSignatureDialogOpen,
    isSubmitting,
    trips,
    setValue,
    getValues,
    createReservationErrors,
    disableSubmit,
    turnstileElement,
    reservationPaymentSetup,
  } = props;

  // derived state
  const {
    bookingContact,
    driverGratuityPctCustomerInput,
    paymentMethod,
    pickUpVariantSelected,
  } = createReservationErrors;

  const validToOpenSignatureDialog =
    !isEmpty(createReservationErrors) &&
    !bookingContact &&
    !driverGratuityPctCustomerInput &&
    !paymentMethod &&
    !pickUpVariantSelected;

  // contexts
  const { isPricelessBooking } = usePricelessBookingContext();

  // hooks
  const {
    operator: {
      enableCreditCardWhenBooking,
      gratuityOptional,
      customGratuityWhenBooking,
      customGratuityNoMinimum,
      pricingLayout,
      settings: { requireSignatureWhenBooking, currencyCode } = {},
    },
  } = useOperator();

  const pricing = useAutomatedPricing({
    trips,
    driverGratuityPctCustomerInput: watch("driverGratuityPctCustomerInput"),
    pickUpVariantSelected: watch("pickUpVariantSelected"),
    userAppliedPromoCode: watch("promoCodeCustomerInput"),
    additonalItemsForOutboundTrip: watch(`additonalItemsByTrip`)[0],
  });

  const { autoPaymentAmt, autoChargeButtonText, autoPaymentType } =
    useAutoChargePayment({ pricings: [pricing], skip: isPricelessBooking });

  const { displayMeetGreet, meetGreetAmt } = useDisplayMeetGreet({
    isAirportPickUpStop:
      !!trips[0].stops[0].airport?.icaoCode ||
      !!trips[1]?.stops[0]?.airport?.icaoCode,
    mode: "create",
    createPickUpAddressIsAirport:
      trips[0].stops[0].pickUpGooglePlaceTypes?.includes("airport") ||
      (trips[0].tripCategory === TripCategory.RoundTrip &&
        trips[1].stops[0].pickUpGooglePlaceTypes?.includes("airport")),
  });

  const isGratuityEnabled = useIsGratuityEnabled({
    trips,
    mode: isPricelessBooking ? "priceless" : "create",
  });

  const { displayAdditionalItems, additionItems } = useDisplayAdditionalItems({
    mode: "create",
    vehicle: trips[0].routes[0].vehicle,
  });

  const baseRateAmt = useMemo(() => {
    let baseRateAmtCents = 0;

    trips.forEach(({ routes }) => {
      const { baseRateAutomation, returnBaseRateAutomation } =
        first(routes)?.vehicle || {};

      const automatedBaseRate = baseRateAutomation?.total || null;
      const returnAutomatedBaseRate = returnBaseRateAutomation?.total || null;

      baseRateAmtCents += cc.toCents(automatedBaseRate || 0);
      baseRateAmtCents += cc.toCents(returnAutomatedBaseRate || 0);
    });

    return cc.toDollars(baseRateAmtCents);
  }, [trips]);

  const getGratuityAmount = useCallback(
    (gratuityPct: number) => {
      const outboundBaseRate =
        first(first(trips).routes)?.vehicle?.baseRateAutomation?.total || 0;
      const returnBaseRate =
        first(first(trips).routes)?.vehicle?.returnBaseRateAutomation?.total ||
        null;
      const commonPricingParams = {
        pricingLayout,
        driverGratuityPctCustomerInput: gratuityPct,
        pickUpVariantSelected: watch("pickUpVariantSelected"),
        userAppliedPromoCode: watch("promoCodeCustomerInput"),
        childSeatInput: watch(`additonalItemsByTrip`)?.[0]?.childSeats,
      };

      const outboundPricing = calculateCreateTripPricing({
        ...commonPricingParams,
        baseRateAmt: outboundBaseRate,
        isAirportPickUpStop:
          !!trips[0].stops[0].trackedFlight ||
          !!trips[0].stops[0].pickUpGooglePlaceTypes?.includes("airport") ||
          !!trips[0].stops[0].airport?.icaoCode,
      });

      const returnDriverGratuity = returnBaseRate
        ? calculateCreateTripPricing({
            ...commonPricingParams,
            baseRateAmt: returnBaseRate,
            isAirportPickUpStop:
              !!trips[1]?.stops[0]?.trackedFlight ||
              !!trips[1]?.stops[0]?.pickUpGooglePlaceTypes?.includes(
                "airport"
              ) ||
              !!trips[1]?.stops[0]?.airport?.icaoCode,
          }).tripPricing.driverGratuity.value || 0
        : 0;

      return (
        (outboundPricing.tripPricing.driverGratuity.value || 0) +
        returnDriverGratuity
      );
    },
    [pricingLayout, trips, watch]
  );

  const pickUpDateTime = trips[0].stops[0].dateTime;

  // effects
  useEffect(() => {
    setValue("autoPaymentAmt", autoPaymentAmt);
    setValue("autoPaymentType", autoPaymentType);
  }, [autoPaymentAmt, setValue, autoPaymentType]);

  return (
    <Card variant="outlined">
      <Box display="flex" flexDirection="column" m={3}>
        {/* Price Overview */}
        {!isPricelessBooking && (
          <PriceOverviewBlock
            pricings={[pricing]}
            orderTotalAmt={pricing.orderTotalAmt}
            pickUpDateTime={pickUpDateTime}
          />
        )}
        {/* Meet & Greet */}
        {displayMeetGreet && (
          <Box my={3}>
            <MeetGreetBlock
              control={control}
              meetGreetAmt={meetGreetAmt}
              currencyCode={currencyCode}
            />
          </Box>
        )}
        {/* Driver Gratuity */}
        {isGratuityEnabled && (
          <Box my={3}>
            <DriverGratuityBlock
              control={control}
              baseRateAmt={baseRateAmt}
              getGratuityAmount={getGratuityAmount}
              isOptional={gratuityOptional}
              customGratuityEnabled={customGratuityWhenBooking}
              customGratuityNoMinimum={customGratuityNoMinimum}
            />
          </Box>
        )}
        {/* Additional Items */}
        {displayAdditionalItems && (
          <Box my={3}>
            <AdditionalItemsBlock
              {...additionItems}
              childSeatsFormPath={`additonalItemsByTrip.0.childSeats`}
            />
          </Box>
        )}
        {/* Divider, only render if any above are rendered */}
        {(!isPricelessBooking ||
          displayMeetGreet ||
          isGratuityEnabled ||
          displayAdditionalItems) && <Divider sx={{ mb: 3 }} />}
        {/* Luggage Count */}
        <LuggageCountBlock
          control={control}
          watch={watch}
          setValue={setValue}
          syncStateWithOutboundTrip={true}
        />
        {/* Booking Contact */}
        <Box mb={3}>
          <BookingContactBlock
            control={control}
            watch={watch}
            trackingFrom="reserve"
            setValue={setValue}
            getValues={getValues}
          />
        </Box>
        {/* Payment Method */}
        {enableCreditCardWhenBooking && (
          <PaymentMethodBlock
            control={control}
            trackingFrom="reserve"
            setValue={setValue}
            paymentElementEnabled={reservationPaymentSetup?.enabled}
            paymentElementResuming={
              !!reservationPaymentSetup?.state?.resume &&
              !reservationPaymentSetup?.state?.result
            }
            onSavedPaymentMethod={reservationPaymentSetup?.onSavedPaymentMethod}
            savedPaymentMethod={reservationPaymentSetup?.state?.result}
            paymentElementReturn={reservationPaymentSetup?.state?.addCardReturn}
            onBeforePaymentElementConfirm={
              reservationPaymentSetup?.onBeforeAddCardConfirm
            }
            onPaymentElementReturnCleared={
              reservationPaymentSetup?.clearAddCardReturn
            }
            paymentElementForm={
              reservationPaymentSetup?.enabled ? (
                <ReservationPaymentForm
                  checkout={reservationPaymentSetup}
                  isSubmitting={isSubmitting}
                />
              ) : undefined
            }
          />
        )}
        {/* Terms and Conditions / Cancellation Policy */}
        <Box mb={3}>
          <TermsAndConditionsBlock
            trips={trips}
            isSubmitting={false}
            control={
              control as Control<
                CreateReservationFormState | ConfirmReservationFormState
              >
            }
          />
        </Box>
        {/* Submit Button */}
        {requireSignatureWhenBooking ? (
          <ConsentSignatureSubmitButton
            isSubmitting={isSubmitting}
            onConsentSignatureDialogOpen={onConsentSignatureDialogOpen}
            autoChargeButtonText={autoChargeButtonText}
            validToOpenSignatureDialog={validToOpenSignatureDialog}
            formId={formId}
            disabled={disableSubmit}
          />
        ) : (
          <SubmitButton
            isSubmitting={isSubmitting}
            formId={formId}
            autoChargeButtonText={autoChargeButtonText}
            disabled={disableSubmit}
          />
        )}
        {turnstileElement}

        {/* SMS Consent Message */}
        <SmsConsentMessage
          actionButtonName={autoChargeButtonText ? "Pay" : "Reserve"}
          typographySx={{ mt: 3 }}
        />

        {/* Your Card Will Not Be Charged */}
        {!autoPaymentAmt && enableCreditCardWhenBooking ? (
          <Box mt={2}>
            <YourCardWillNotBeChargedBlock />
          </Box>
        ) : null}
      </Box>
    </Card>
  );
}

export default CreateReservationFormFields;
